/**
 * Gamificación de FamiDex — RANGOS y matriz de XP (capa de dominio PURA).
 *
 * El núcleo (`xp.ts`/`engine.ts`) es agnóstico; aquí se declara QUÉ acciones dan
 * XP (matriz server-authoritative) y los 6 RANGOS por umbral. El rango se DERIVA
 * de la XP acumulada con la curva por umbrales de `gamification-core`
 * (`thresholdCurve`) — no se persiste ningún nivel, es un reducer determinista
 * que corre en el cliente (local-first) y se re-verifica en el servidor.
 *
 * Receta GAMIF-3: `toSorted` / `!` (nunca `.sort()` / `as`).
 */
import { levelForXp, levelProgress, thresholdCurve } from "./xp";

/** Acciones de dominio que otorgan XP. La cantidad la fija la matriz. */
export type XpAction =
  | "add_item" // añadir un producto a la colección
  | "friend" // seguidor recíproco (amigo)
  | "post" // crear una publicación
  | "like_received" // recibir un like
  | "like_given" // dar un like
  | "daily_login"; // login diario

/**
 * MATRIZ DE XP — la única fuente de verdad de cuánta XP da cada acción. El
 * servidor la re-aplica al procesar el ledger (antifraude: el cliente no puede
 * forjar la cantidad). Debe mantenerse alineada con `XP_MATRIX` en
 * `core-rust/src/db/sync.rs`.
 */
export const XP_MATRIX: Record<XpAction, number> = {
  add_item: 50,
  friend: 100,
  post: 30,
  like_received: 5,
  like_given: 2,
  daily_login: 10,
};

/** XP que otorga una acción (0 si el tipo es desconocido). */
export function xpForAction(action: string): number {
  // Índice por `string` (widening seguro) para evitar un `as XpAction` (GAMIF-3).
  const table: Record<string, number> = XP_MATRIX;
  return table[action] ?? 0;
}

/** Recompensa desbloqueada al alcanzar un rango (clave i18n + icono). */
export interface RankReward {
  /** Clave semántica de la recompensa (la UI la traduce). */
  key: string;
  /** Nombre de icono (Material Symbols / lucide) sugerido. */
  icon: string;
}

/** Definición de un rango de coleccionista. */
export interface RankDef {
  /** Identificador estable (no traducible). */
  id: string;
  /** XP acumulada mínima para entrar en el rango. */
  minXp: number;
  /** Icono sugerido para el rango. */
  icon: string;
  /** Recompensas que desbloquea. */
  rewards: RankReward[];
}

/**
 * Los 6 rangos, en orden ascendente (ver infra/products/famidex.md §1):
 *   Recolector Novato 0 · Guía del Bosque 501 · Buscador de Estancias 1501 ·
 *   Catalogador Maestro 4001 · Gran Conservador 8001 · Venerable Anciano 15001.
 * El `id` es estable; el nombre y la descripción los traduce la UI por `id`.
 */
export const RANKS: readonly RankDef[] = [
  { id: "novato", minXp: 0, icon: "shopping_basket", rewards: [] },
  {
    id: "guia_bosque",
    minXp: 501,
    icon: "emoji_objects",
    rewards: [{ key: "custom_badge", icon: "military_tech" }],
  },
  {
    id: "buscador_estancias",
    minXp: 1501,
    icon: "home",
    rewards: [{ key: "bronze_border", icon: "frame_person" }],
  },
  {
    id: "catalogador_maestro",
    minXp: 4001,
    icon: "auto_stories",
    rewards: [
      { key: "shop_discount_15", icon: "sell" },
      { key: "oak_frame", icon: "frame_person" },
    ],
  },
  {
    id: "gran_conservador",
    minXp: 8001,
    icon: "museum",
    rewards: [{ key: "exclusive_stickers", icon: "auto_awesome" }],
  },
  {
    id: "venerable_anciano",
    minXp: 15001,
    icon: "ac_unit",
    rewards: [{ key: "golden_glow", icon: "auto_awesome" }],
  },
];

/** Nº de rangos (constante de dominio). */
export const RANK_COUNT = RANKS.length;

/** Umbrales de XP por rango, ordenados ascendente (índice 0 = rango 1 = 0 XP). */
export const RANK_THRESHOLDS: readonly number[] = RANKS.map((r) => r.minXp).toSorted(
  (a, b) => a - b,
);

/**
 * Curva por umbrales de `gamification-core`: nivel N (1..6) ⇒ rango N. Se reusa
 * para derivar el rango y el progreso con el mismo motor puro del kit.
 */
export const FAMIDEX_RANK_CURVE = thresholdCurve(RANK_THRESHOLDS);

/** Índice del rango (0..RANK_COUNT-1) para una XP acumulada. Saturado al máximo. */
export function rankIndexForXp(xp: number): number {
  // `levelForXp` devuelve 1..N con la curva de umbrales; lo acotamos al último
  // rango (la curva extrapola más allá del máximo, pero no hay rango 7).
  const level = levelForXp(Math.max(0, xp), FAMIDEX_RANK_CURVE);
  return Math.min(RANK_COUNT - 1, Math.max(0, level - 1));
}

/** El rango que corresponde a una XP acumulada. */
export function rankForXp(xp: number): RankDef {
  return RANKS[rankIndexForXp(xp)]!;
}

/** Progreso dentro de la jerarquía de rangos, derivado de la XP acumulada. */
export interface RankProgress {
  /** Índice del rango actual (0-based). */
  index: number;
  /** Rango actual. */
  rank: RankDef;
  /** Siguiente rango, o `null` si ya es el máximo. */
  nextRank: RankDef | null;
  /** XP dentro del rango actual (acumulada - umbral del rango). */
  intoRank: number;
  /** XP que abarca el rango actual→siguiente (0 en el máximo). */
  rankSpan: number;
  /** 0..1 hacia el siguiente rango (1 en el máximo). */
  ratio: number;
  /** XP que falta para el siguiente rango (0 en el máximo). */
  xpToNext: number;
  /** true si ya está en el rango máximo. */
  isMax: boolean;
  /** XP acumulada total. */
  totalXp: number;
}

/**
 * Deriva el progreso de rango de la XP acumulada. Reusa `levelProgress` del kit
 * para el tramo intermedio y trata el rango máximo como "completo" (sin
 * siguiente). Puro y determinista.
 */
export function rankProgress(xp: number): RankProgress {
  const totalXp = Math.max(0, Math.floor(xp));
  const index = rankIndexForXp(totalXp);
  const rank = RANKS[index]!;
  const isMax = index >= RANK_COUNT - 1;
  const nextRank = isMax ? null : RANKS[index + 1]!;

  if (isMax || !nextRank) {
    const intoRank = totalXp - rank.minXp;
    return {
      index,
      rank,
      nextRank: null,
      intoRank: Math.max(0, intoRank),
      rankSpan: 0,
      ratio: 1,
      xpToNext: 0,
      isMax: true,
      totalXp,
    };
  }

  // `levelProgress` con la curva de umbrales mide dentro del "nivel"=rango actual.
  const lp = levelProgress(totalXp, FAMIDEX_RANK_CURVE);
  const rankSpan = Math.max(1, nextRank.minXp - rank.minXp);
  return {
    index,
    rank,
    nextRank,
    intoRank: Math.max(0, totalXp - rank.minXp),
    rankSpan,
    ratio: lp.ratio,
    xpToNext: Math.max(0, nextRank.minXp - totalXp),
    isMax: false,
    totalXp,
  };
}

/** Estado de un rango relativo a la XP del usuario (para la línea de tiempo). */
export interface RankStatus {
  rank: RankDef;
  index: number;
  /** true si el usuario ya alcanzó este rango. */
  unlocked: boolean;
  /** true si es el rango actual del usuario. */
  current: boolean;
  /** XP que falta para desbloquearlo (0 si ya desbloqueado). */
  xpRemaining: number;
}

/** Estado de TODOS los rangos para pintar la jerarquía (desbloqueado/actual/bloqueado). */
export function allRanksStatus(xp: number): RankStatus[] {
  const totalXp = Math.max(0, Math.floor(xp));
  const currentIndex = rankIndexForXp(totalXp);
  return RANKS.map((rank, index) => ({
    rank,
    index,
    unlocked: totalXp >= rank.minXp,
    current: index === currentIndex,
    xpRemaining: Math.max(0, rank.minXp - totalXp),
  }));
}

/**
 * Percentil de PROGRESIÓN a lo largo de toda la escalera de rangos: 0 en 0 XP,
 * 100 al alcanzar el rango máximo. Es un resumen estable de "cuán avanzado" está
 * el coleccionista dentro de la jerarquía completa (no depende de otros
 * usuarios). Entero 0..100.
 */
export function rankPercentile(xp: number): number {
  const maxThreshold = RANK_THRESHOLDS[RANK_THRESHOLDS.length - 1]!;
  if (maxThreshold <= 0) {
    return 100;
  }
  const clamped = Math.max(0, Math.min(maxThreshold, Math.floor(xp)));
  return Math.round((clamped / maxThreshold) * 100);
}
